import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import { useAccount } from '../../hooks/useAccount'
import EcommerceIntro from './EcommerceIntro'
import EcommerceUsageIndicator from './EcommerceUsageIndicator'

function StatCard({ title, value, subtitle, linkTo, linkText, icon }) {
  return (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-3xl font-bold text-primary-600">{value}</p>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        {icon && <div className="text-gray-400">{icon}</div>}
      </div>
      {linkTo && (
        <Link
          to={linkTo}
          className="inline-block mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          {linkText} &rarr;
        </Link>
      )}
    </div>
  )
}

function EcommerceDashboard() {
  const { t } = useTranslation('ecommerce')
  const { selectedAccount } = useAccount()
  const [stats, setStats] = useState({
    feedCount: 0,
    productCount: 0,
    bioPageConfigured: false,
    bioPageId: null,
    clicksToday: 0,
  })
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    if (!selectedAccount?.id) return

    try {
      setLoading(true)

      const [feedsResult, usageResult, bioPagesResult] = await Promise.all([
        api.getFeeds().catch(() => ({ feeds: [] })),
        api.getEcommerceUsage().catch(() => ({})),
        api.getBioPages().catch(() => ({ bio_pages: [] })),
      ])

      // Bio pages are already filtered by account on the backend
      const bioPages = bioPagesResult.bio_pages || []
      const bioPage = bioPages[0] // One bio page per account

      setStats({
        feedCount: usageResult.feeds?.used ?? feedsResult.feeds?.length ?? 0,
        productCount: usageResult.products?.used ?? 0,
        bioPageConfigured: !!bioPage,
        bioPageId: bioPage?.bio_page_id || null,
        clicksToday: 0,
      })
    } catch (error) {
      console.error('Failed to fetch e-commerce stats:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedAccount?.id])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="loading-spinner" role="status">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="sr-only">{t('common.loading', 'Loading...')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard.title', 'E-Commerce')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('dashboard.subtitle', 'Manage your product feeds and bio pages')}
          </p>
        </div>
        <Link
          to="/ecommerce/feeds/new"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('dashboard.addFeed', 'Add Feed')}
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title={t('dashboard.feeds', 'Product Feeds')}
          value={stats.feedCount}
          subtitle={t('dashboard.feedsConfigured', 'feeds configured')}
          linkTo="/ecommerce/feeds"
          linkText={t('dashboard.manageFeed', 'Manage feeds')}
        />

        <StatCard
          title={t('dashboard.products', 'Products')}
          value={stats.productCount}
          subtitle={t('dashboard.productsImported', 'products imported')}
          linkTo="/ecommerce/products"
          linkText={t('dashboard.viewProducts', 'View products')}
        />

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('dashboard.bioPage', 'Bio Page')}
          </h3>
          {stats.bioPageConfigured ? (
            <>
              <p className="text-sm text-green-600 font-medium">
                {t('dashboard.bioPageActive', 'Active')}
              </p>
              <Link
                to={`/ecommerce/bio-pages/${stats.bioPageId}`}
                className="inline-block mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t('dashboard.editBioPage', 'Edit bio page')} &rarr;
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500">
                {t('dashboard.bioPageNotConfigured', 'Not configured yet')}
              </p>
              <Link
                to="/ecommerce/bio-pages/new"
                className="inline-block mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                {t('dashboard.setupBioPage', 'Set up bio page')} &rarr;
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Next Step Hint */}
      {stats.feedCount === 0 ? (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-800">
                {t('dashboard.nextStep.importFeed', 'Next step: Import your first product feed')}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {t('dashboard.nextStep.importFeedDesc', 'Connect your Google Shopping or Facebook Catalog feed to get started.')}
              </p>
              <Link
                to="/ecommerce/feeds/new"
                className="inline-flex items-center mt-2 text-sm font-medium text-blue-700 hover:text-blue-900"
              >
                {t('dashboard.nextStep.importFeedCta', 'Import feed')} &rarr;
              </Link>
            </div>
          </div>
        </div>
      ) : stats.feedCount > 0 && !stats.bioPageConfigured ? (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-800">
                {t('dashboard.nextStep.bioPage', 'Next step: Set up your Bio Page')}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {t('dashboard.nextStep.bioPageDesc', 'Create a landing page so your followers can find your products via Link in Bio.')}
              </p>
              <Link
                to="/ecommerce/bio-pages/new"
                className="inline-flex items-center mt-2 text-sm font-medium text-blue-700 hover:text-blue-900"
              >
                {t('dashboard.nextStep.bioPageCta', 'Set up Bio Page')} &rarr;
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {/* Usage Indicator */}
      <EcommerceUsageIndicator />

      {/* Intro & FAQ when no feeds configured */}
      {stats.feedCount === 0 && (
        <div className="space-y-8">
          <EcommerceIntro compact />

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/ecommerce/feeds/new"
              className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('dashboard.empty.cta', 'Import your first feed')}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string.isRequired,
  linkTo: PropTypes.string,
  linkText: PropTypes.string,
  icon: PropTypes.node,
}

export default EcommerceDashboard
